/**
 * Netto Deals Service
 * Service to fetch and analyze current Netto promotions
 */

export interface NettoDeal {
  product: string;           // Nom du produit
  brand?: string;            // Marque
  category: string;          // Catégorie
  discount: string;          // Type de remise
  price?: string;            // Prix promo
  originalPrice?: string;    // Prix de base (barré)
  size?: string;             // Format/Taille (ex: 1L, 500g, pack de 6)
  description?: string;      // Description complémentaire
  location?: string;         // Où trouver en magasin
  barcode?: string;          // Code-barres (EAN)
  validUntil: string;        // Date de fin de promo
}

/**
 * Current Netto deals (week 49-50, December 2025)
 * Based on actual promotions from Netto catalogs
 */
const CURRENT_DEALS: NettoDeal[] = [
  // Produits de fêtes (valable jusqu'au 08/12/2025)
  { 
    product: 'Foie gras de canard entier', 
    brand: 'Comtesse du Barry',
    category: 'Traiteur', 
    discount: 'Promo spéciale fêtes', 
    size: '120g',
    description: 'Foie gras entier IGP du Sud-Ouest',
    location: 'Rayon traiteur, allée fêtes',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Escargots de Bourgogne', 
    brand: 'Netto',
    category: 'Traiteur', 
    discount: 'Prix bas', 
    size: 'Boîte de 12',
    description: 'Préparés au beurre persillé',
    location: 'Rayon traiteur, allée fêtes',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Gigot d\'agneau surgelé', 
    brand: 'Charal',
    category: 'Boucherie', 
    price: '11,89€/kg', 
    discount: 'Promo', 
    size: '1,2 kg environ',
    description: 'Origine France, décongelé en 6h au réfrigérateur',
    location: 'Rayon boucherie, congélateur viandes',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Haricots verts extra-fins surgelés', 
    brand: 'Bonduelle',
    category: 'Surgelés', 
    price: '1,55€', 
    originalPrice: '1,85€',
    discount: '-16%', 
    size: '1kg',
    description: 'Extra-fins, cueillis et surgelés le même jour',
    location: 'Rayon surgelés, allée légumes',
    validUntil: '2025-12-08' 
  },
  
  // Apéritif et boissons
  { 
    product: 'Apéricube original', 
    brand: 'Bel',
    category: 'Frais', 
    discount: '-60% sur le 2ème', 
    size: '125g (48 cubes)',
    description: 'Fromage fondu en cubes pour l\'apéro',
    location: 'Rayon frais, allée fromages',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Chips Lay\'s Paysannes', 
    brand: 'Lay\'s',
    category: 'Épicerie', 
    price: '1,47€', 
    originalPrice: '2,23€',
    discount: '-34%', 
    size: 'Sachet 150g',
    description: 'Saveur nature, sel de Guérande',
    location: 'Rayon épicerie salée, allée apéritif',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Chips Bret\'s', 
    brand: 'Bret\'s',
    category: 'Épicerie', 
    price: '1,47€', 
    discount: '-34%', 
    size: '125g',
    description: 'Différents parfums disponibles',
    location: 'Rayon épicerie salée, allée apéritif',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Desperados bière aromatisée tequila', 
    brand: 'Desperados',
    category: 'Boissons', 
    discount: 'Promo', 
    size: 'Pack 6x33cl',
    description: 'Bière aromatisée tequila, 5,9% vol.',
    location: 'Rayon boissons, allée bières',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Heineken bière blonde', 
    brand: 'Heineken',
    category: 'Boissons', 
    price: '0,55€', 
    discount: 'Prix bas', 
    size: 'Bouteille 25cl',
    description: 'Bière blonde premium, 5% vol.',
    location: 'Rayon boissons, allée bières',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Kronenbourg 1664 bière blonde', 
    brand: '1664',
    category: 'Boissons', 
    price: '0,55€', 
    discount: 'Prix bas', 
    size: 'Canette 33cl',
    description: 'Bière blonde française, 5,5% vol.',
    location: 'Rayon boissons, allée bières',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Pur jus de pomme', 
    brand: 'Jafaden',
    category: 'Boissons', 
    price: '1,87€', 
    discount: 'Promo', 
    size: '1L',
    description: '100% pur jus de pomme, sans sucres ajoutés',
    location: 'Rayon boissons, allée jus de fruits',
    validUntil: '2025-12-08' 
  },
  { 
    product: 'Tropicana Pure Premium orange', 
    brand: 'Tropicana',
    category: 'Boissons', 
    price: '2,99€', 
    discount: 'Promo', 
    size: '1,5L',
    description: 'Jus d\'orange pur jus, pulpe ou sans pulpe',
    location: 'Rayon frais, allée jus',
    validUntil: '2025-12-08' 
  },
  
  // Épicerie
  { 
    product: 'Pâtes Panzani Spaghetti', 
    brand: 'Panzani',
    category: 'Épicerie', 
    discount: 'Prix compétitifs', 
    size: '500g',
    description: 'Spaghetti n°5, cuisson 9 min',
    location: 'Rayon épicerie, allée pâtes riz',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Pâtes Barilla Penne Rigate', 
    brand: 'Barilla',
    category: 'Épicerie', 
    discount: 'Prix compétitifs', 
    size: '500g',
    description: 'Penne rigate n°73, cuisson 11 min',
    location: 'Rayon épicerie, allée pâtes riz',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Café moulu Carte Noire Classique', 
    brand: 'Carte Noire',
    category: 'Épicerie', 
    discount: 'Promo', 
    size: '250g',
    description: 'Café moulu, arabica, intensité 4/5',
    location: 'Rayon épicerie, allée cafés',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Chocolat Nestlé Dessert noir', 
    brand: 'Nestlé',
    category: 'Épicerie', 
    discount: 'Promo', 
    size: '205g (2 tablettes)',
    description: 'Chocolat à pâtisser noir supérieur, 52% cacao',
    location: 'Rayon épicerie sucrée, allée chocolat/gâteaux',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Chocolat Lindt tablette Excellence noir 70%', 
    brand: 'Lindt',
    category: 'Épicerie', 
    discount: 'Promo', 
    size: '100g',
    description: 'Chocolat noir 70% cacao, dégustation',
    location: 'Rayon épicerie sucrée, allée chocolat',
    validUntil: '2025-12-15' 
  },
  
  // Frais
  { 
    product: 'Lait demi-écrémé Lactel', 
    brand: 'Lactel',
    category: 'Frais', 
    discount: 'Prix bas', 
    size: '1L',
    description: 'Lait UHT demi-écrémé, Omega 3',
    location: 'Rayon frais, allée produits laitiers',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Cuisses de poulet jaune', 
    brand: 'Maître CoQ',
    category: 'Boucherie', 
    discount: 'Promo', 
    size: 'Barquette 1kg environ',
    description: 'Poulet jaune français, élevé sans antibiotiques',
    location: 'Rayon boucherie, vitrines réfrigérées',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Filet de poulet', 
    brand: 'Maître CoQ',
    category: 'Boucherie', 
    discount: 'Promo', 
    size: '600g',
    description: 'Filets de poulet blanc, origine France',
    location: 'Rayon boucherie, vitrines réfrigérées',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Œufs frais de poules élevées en plein air', 
    brand: 'Netto Bio',
    category: 'Frais', 
    discount: 'Prix bas', 
    size: 'Boîte de 6 gros œufs',
    description: 'Œufs code 1 (plein air), calibre L',
    location: 'Rayon frais, allée œufs',
    validUntil: '2025-12-15' 
  },
  
  // Droguerie
  { 
    product: 'Lessive liquide X-Tra Total', 
    brand: 'X-Tra',
    category: 'Droguerie', 
    discount: 'Promo', 
    size: '3L (60 lavages)',
    description: 'Lessive liquide concentrée, tous textiles',
    location: 'Rayon droguerie, allée lessives',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Lessive Skip Ultimate', 
    brand: 'Skip',
    category: 'Droguerie', 
    discount: 'Promo', 
    size: '1,5L (30 lavages)',
    description: 'Lessive liquide anti-odeurs tenaces',
    location: 'Rayon droguerie, allée lessives',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Papier toilette Lotus Just 1', 
    brand: 'Lotus',
    category: 'Droguerie', 
    discount: 'Remise', 
    size: 'Pack de 9 rouleaux',
    description: 'Papier toilette 3 plis, décoré',
    location: 'Rayon droguerie, allée papiers',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Papier toilette Okay', 
    brand: 'Okay',
    category: 'Droguerie', 
    discount: 'Remise', 
    size: 'Pack de 12 rouleaux',
    description: 'Papier toilette blanc 2 plis',
    location: 'Rayon droguerie, allée papiers',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Head & Shoulders shampooing antipelliculaire', 
    brand: 'Head & Shoulders',
    category: 'Hygiène', 
    discount: 'Promo', 
    size: '250ml',
    description: 'Shampooing Classic Clean',
    location: 'Rayon hygiène, allée shampooings',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'L\'Oréal Elseve shampooing', 
    brand: 'L\'Oréal',
    category: 'Hygiène', 
    discount: 'Promo', 
    size: '250ml',
    description: 'Différentes gammes disponibles',
    location: 'Rayon hygiène, allée shampooings',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Anti dépôts Eden lave-vaisselle', 
    brand: 'Eden',
    category: 'Droguerie', 
    price: '0,99€', 
    originalPrice: '1,39€',
    discount: '-28%', 
    size: '5L',
    description: 'Liquide de rinçage lave-vaisselle',
    location: 'Rayon droguerie, allée lave-vaisselle',
    validUntil: '2025-12-15' 
  },
  
  // Fruits & Légumes
  { 
    product: 'Pommes de terre Agata', 
    brand: 'Française',
    category: 'Fruits & Légumes', 
    discount: 'Promo', 
    size: 'Filet 2,5kg',
    description: 'Variété Agata, chair ferme, idéale vapeur/salade',
    location: 'Rayon fruits & légumes, étal pommes de terre',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Pommes de terre Charlotte', 
    brand: 'Française',
    category: 'Fruits & Légumes', 
    discount: 'Promo', 
    size: 'Sachet 1kg',
    description: 'Variété Charlotte, chair ferme',
    location: 'Rayon fruits & légumes, étal pommes de terre',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Tomates rondes', 
    brand: 'Espagne',
    category: 'Fruits & Légumes', 
    discount: 'Promo', 
    size: 'Au kg',
    description: 'Tomates rondes, catégorie 1',
    location: 'Rayon fruits & légumes, étal tomates',
    validUntil: '2025-12-15' 
  },
  { 
    product: 'Tomates grappe', 
    brand: 'France',
    category: 'Fruits & Légumes', 
    discount: 'Promo', 
    size: 'Barquette 500g',
    description: 'Tomates grappe françaises, saveur intense',
    location: 'Rayon fruits & légumes, étal tomates',
    validUntil: '2025-12-15' 
  },
];

/**
 * Check if an item name matches a current promotion
 */
export function isItemOnPromo(itemName: string): boolean {
  const today = new Date();
  const normalizedItem = itemName.toLowerCase();
  
  return CURRENT_DEALS.some(deal => {
    const validUntil = new Date(deal.validUntil);
    if (validUntil < today) return false;
    
    const normalizedDeal = deal.product.toLowerCase();
    const normalizedBrand = deal.brand?.toLowerCase() || '';
    return normalizedDeal.includes(normalizedItem) || 
           normalizedItem.includes(normalizedDeal) ||
           normalizedBrand.includes(normalizedItem);
  });
}

/**
 * Find deals matching items from shopping list
 */
export function findMatchingDeals(itemNames: string[]): NettoDeal[] {
  const today = new Date();
  
  return CURRENT_DEALS.filter(deal => {
    // Check if deal is still valid
    const validUntil = new Date(deal.validUntil);
    if (validUntil < today) return false;
    
    // Check if any item name matches (case insensitive, partial match)
    return itemNames.some(itemName => {
      const normalizedItem = itemName.toLowerCase();
      const normalizedDeal = deal.product.toLowerCase();
      const normalizedBrand = deal.brand?.toLowerCase() || '';
      return normalizedDeal.includes(normalizedItem) || 
             normalizedItem.includes(normalizedDeal) ||
             normalizedBrand.includes(normalizedItem);
    });
  });
}

/**
 * Get all current valid deals
 */
export function getCurrentDeals(): NettoDeal[] {
  const today = new Date();
  return CURRENT_DEALS.filter(deal => {
    const validUntil = new Date(deal.validUntil);
    return validUntil >= today;
  });
}

/**
 * Get deals by category
 */
export function getDealsByCategory(category: string): NettoDeal[] {
  const today = new Date();
  return CURRENT_DEALS.filter(deal => {
    const validUntil = new Date(deal.validUntil);
    return validUntil >= today && deal.category.toLowerCase() === category.toLowerCase();
  });
}
